import {OktaAuth, toRelativeUrl} from '@okta/okta-auth-js';
import {LoginCallback, Security} from '@okta/okta-react';
import {RestoreOriginalUriFunction} from '@okta/okta-react/bundles/types/OktaContext';
import {Alert, Link as EvergreenLink, Paragraph} from 'evergreen-ui';
import React, {ReactNode, useCallback, useState} from 'react';
import {Route, Switch} from 'react-router';
import {Link as RouterLink, useHistory} from 'react-router-dom';
import {NamedOAuth2Config} from 'security';


type SecurityProps = {
    oauth2Config: NamedOAuth2Config,
    children: ReactNode,
};
export const OktaSecurity = ({
                                 oauth2Config,
                                 children,
                             }: SecurityProps) => {
    const [oktaAuth] = useState(() => new OktaAuth({
        issuer: oauth2Config.issuer,
        clientId: oauth2Config.clientId,
        redirectUri: `${window.location.origin}/${oauth2Config.name}/oauth2/callback`,
        scopes: oauth2Config.scopes,
        pkce: oauth2Config.pkce,
        tokenManager: {
            secure: true,
            storage: 'sessionStorage',
            storageKey: `${oauth2Config.name}-okta-token-storage`,
        }
    }));

    const history = useHistory();
    const restoreOriginalUri: RestoreOriginalUriFunction = useCallback(async (_oktaAuth, originalUri) => {
        history.replace(toRelativeUrl(originalUri, window.location.origin));
    }, [history]);

    return (
        <Security
            oktaAuth={oktaAuth}
            restoreOriginalUri={restoreOriginalUri}
        >
            <Switch>
                <Route path={`/${oauth2Config.name}/oauth2/callback`} exact
                       children={
                           <LoginCallback errorComponent={
                               ({error}: { error: OktaError }) =>
                                   <OktaErrorMessage error={error} reloadUrl={`/${oauth2Config.name}`}
                                   />
                           }/>
                       }
                />
                {children}
            </Switch>
        </Security>
    );
}


type OktaError = {
    name: string;
    message: string;
    errorSummary?: string;
    errorCode?: string;
    errorLink?: string;
}
type OktaErrorMessageProps = {
    error: OktaError;
    reloadUrl: string;
};
const OktaErrorMessage = ({error, reloadUrl}: OktaErrorMessageProps) => {
    return (
        <div className='items-stretch h-screen flex'>
            <Alert margin='auto' alignSelf='center' intent='danger'
                   title='Unable to authenticate'
            >
                <Paragraph>{error.message}</Paragraph>
                <Paragraph>Please refresh the page or <EvergreenLink size={400} is={RouterLink} to={reloadUrl}>click
                    here</EvergreenLink>.
                </Paragraph>
            </Alert>
        </div>
    );
};
